import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {
  ImportModuleIncompleteCard, ImportModuleLackingRight, ImportModuleMediaOverview, ImportModulePreflightResult,
  ImportModulePreflightRow,
} from '../../../core/definitions/import-module';
import {createdRevisionKindLabel, createdRevisionParts} from '../created-revision';
import {downloadCsv} from '../csv-download';
import {kulturnavRefText} from '../kulturnav-ref';
import {mediaPatternErrorText} from '../media-pattern-error';
import {MEDIA_VALUES_TITLE_KEY} from '../live-preview/live-preview';
import {ImportView} from '../step-bar/import-steps';
import {RowCount, rowCountText, showMore} from '../row-count';
import {
  cardLabels,
  EventGroupKind,
  groupWarnings,
  PreflightErrorSplit,
  RowWarning,
  rowWarnings,
  valueLabel,
  WarningGroup,
} from './preflight-warnings';

type PreflightErrors = ImportModulePreflightResult['errors'];

/** An entry with the label the panel shows for it. */
export interface Labelled<T> {
  entry: T;
  label: string;
}

/** An event-group key with its card's title as the card shows it; `null`
 * for a key no card has, which the panel leaves out. */
export interface TitledEventKey {
  key: string;
  title: string | null;
}

type ComposedEntry = ImportModulePreflightRow['composed'][number];

/** A row's value with its label, as the panel shows it (`text`), and the
 * two together in full (`title`). A KulturNav objectid shows by its
 * entity (`kulturnavRefText`), and in full in `title`. `infoKey` is the
 * note under the value, taking `value`, for a party name the run creates
 * (`will_create`) or takes one of several records for (`ambiguous`);
 * `null` for any other value. `warned`: a warning of the row has the
 * value's label, card and sub-block, so the value stands out among the
 * row's values. */
export interface ComposedValueView extends Labelled<ComposedEntry> {
  text: string;
  title: string;
  infoKey: string | null;
  warned: boolean;
}

type PartyWarning = ImportModulePreflightRow['party_warnings'][number];

// The note a party name shows under its value, by the reason that is
// information rather than a warning.
const PARTY_INFO_KEYS: Readonly<Partial<Record<PartyWarning['reason'], string>>> = {
  will_create: 'TRANS__IMPORT__PREFLIGHT_PARTY_WILL_CREATE',
  ambiguous: 'TRANS__IMPORT__PREFLIGHT_PARTY_AMBIGUOUS',
};

/** A row with the labels its values and warnings show: the object's
 * values in `composed`, and those written onto the linked media in
 * `mediaComposed`, each under «Bilde». */
export interface PreflightRowView {
  row: ImportModulePreflightRow;
  composed: ComposedValueView[];
  mediaComposed: ComposedValueView[];
  events: TitledEventKey[];
  warnings: RowWarning[];
}

/** Which rows a result covers: the first and last row checked, and the
 * rows in the range it was run over when known, as shown ("≈ 356" for an
 * approximate count). `all`: the result holds every row of that range. */
export interface PreflightCoverage {
  checked: number;
  all: boolean;
  total: string | null;
  firstRow: string | null;
  lastRow: string | null;
}

/**
 * What the panel shows for one result.
 * - `errors`: the block-level errors the run refuses as well, a target
 *   field by its label.
 * - `notRefusingErrors`: the other block-level errors, labelled alike.
 * - `alwaysFiringEventGroups`: the event groups that fire on every row
 *   regardless of that row's own mapped values. A row's own `composed`
 *   list may hold nothing related to such a group, so they get one
 *   block-level notice instead of a note on each row.
 * - `rows`: every checked row, in row order.
 * - `warningRows`: the rows with a warning, in row order. Creating an
 *   event is not a warning, so a row that only names an event is in
 *   `rows` alone.
 * - `groups`: the warning types, each with its rows.
 * - `mediaUnchecked`: see the field.
 */
export interface PreflightView {
  errors: string[];
  notRefusingErrors: string[];
  alwaysFiringEventGroups: TitledEventKey[];
  rows: PreflightRowView[];
  warningRows: PreflightRowView[];
  groups: WarningGroup[];
  // Some row's media join was not looked up (`unknown`), said once.
  mediaUnchecked: boolean;
}

const EMPTY_VIEW: PreflightView = {
  errors: [], notRefusingErrors: [], alwaysFiringEventGroups: [], rows: [], warningRows: [], groups: [],
  mediaUnchecked: false,
};

/** Rows with warnings listed before the first "show more". */
export const WARNING_ROWS_FIRST_SHOWN = 5;
/** Rows one "show more" adds, and the rows the list of every checked row
 * shows at a time. */
export const ROWS_STEP = 25;

/**
 * A Forhåndskontroll (preflight) result: tiles that count the checked
 * rows, the block-level errors and notices, how the media folder's files
 * and the rows meet, the warnings grouped by type with where each is
 * fixed, the rows with warnings, and every checked row with its values as
 * they will be saved.
 */
@Component({
  selector: 'app-import-preflight-result',
  templateUrl: './import-preflight-result.component.html',
  styleUrls: ['./import-preflight-result.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportPreflightResultComponent implements OnChanges {
  @Input({required: true}) preflightResult!: ImportModulePreflightResult;
  /** Shows the notice that the rows with no single media match block the
   * run. */
  @Input() mediaBlocksRun = false;
  /** The media join matched by a pattern: a row's media shows the key the
   * pattern made of its cell. */
  @Input() mediaPatternMode = false;
  /** A target field's display label, from its id. */
  @Input({required: true}) resolveLabel!: (fieldId: string) => string;
  /** The title of the card a key belongs to, as the card shows it: an
   * event card's by the kind `event_groups` gives its key, a flat group
   * card's with kind `null`. `null` for a key no card has, which is then
   * left out. */
  @Input({required: true}) resolveCardTitle!: (key: string, kind: EventGroupKind | null) => string | null;
  /** The name of a sub-block of the card `cardKey`, as the card names it,
   * where the card has more than one sub-block of its kind; `null`
   * otherwise. */
  @Input({required: true}) resolveSubBlockTitle!: (cardKey: string, subBlock: string) => string | null;
  /** A result's block-level errors, split by whether the run refuses
   * them. */
  @Input({required: true}) splitErrors!: (result: ImportModulePreflightResult) => PreflightErrorSplit;
  /** The line an `incomplete_cards` entry shows. */
  @Input({required: true}) describeIncompleteCard!: (entry: ImportModuleIncompleteCard) => string;
  /** The line a `rights` entry shows. */
  @Input({required: true}) describeLackingRight!: (entry: ImportModuleLackingRight) => string;

  /** The source column media are joined on; without one the result
   * says nothing of a media folder. */
  @Input() mediaJoinColumn: string | null = null;
  /** The source file's name, which the CSV of the files without a row is
   * named after. */
  @Input() fileName = '';
  /** The rows the range this result was run over holds, when known
   * apart from the result. */
  @Input() rangeCount: RowCount | null = null;
  /** Asks for the step view where a warning type is fixed. */
  @Output() openView = new EventEmitter<ImportView>();

  readonly createdRevisionParts = createdRevisionParts;
  readonly rowsStep = ROWS_STEP;

  /** What the panel shows for the current result, built once per result. */
  view: PreflightView = EMPTY_VIEW;
  /** Which rows the current result covers. */
  coverage: PreflightCoverage = {checked: 0, all: true, total: '0', firstRow: null, lastRow: null};
  /** The warning type the row list is narrowed to; `null` for all. */
  selectedGroup: WarningGroup | null = null;
  /** The rows with warnings the list holds: all, or the selected type's. */
  listedRows: PreflightRowView[] = [];
  /** How many of `listedRows` are shown. */
  shownRows = WARNING_ROWS_FIRST_SHOWN;
  /** The rows in the warnings list whose field values are open, by row. */
  readonly openRows = new Set<string>();
  /** The list of every checked row is open. */
  allRowsOpen = false;
  /** How many rows the list of every checked row shows. */
  allRowsShown = ROWS_STEP;

  private readonly translate = inject(TranslateService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preflightResult'] || changes['resolveLabel'] || changes['resolveCardTitle']
        || changes['resolveSubBlockTitle'] || changes['splitErrors'] || changes['describeIncompleteCard']
        || changes['describeLackingRight']) {
      this.view = this.buildView();
      this.selectedGroup = null;
      this.listedRows = this.view.warningRows;
      this.shownRows = WARNING_ROWS_FIRST_SHOWN;
      this.openRows.clear();
      this.allRowsOpen = false;
      this.allRowsShown = ROWS_STEP;
    }
    if (changes['preflightResult'] || changes['rangeCount']) {
      this.coverage = this.buildCoverage();
    }
  }

  /** A join column is set but the result has no media overview, and no
   * media error or refused media match pattern says why: no media folder
   * was chosen. */
  get mediaFolderMissing(): boolean {
    const result = this.preflightResult;
    return !!this.mediaJoinColumn && !result.media_overview && !result.errors?.media?.length
      && !result.errors?.media_pattern;
  }

  /** How many of a capped list's entries it leaves out; 0 once it shows
   * every entry. */
  droppedCount(total: number, shown: number): number {
    return Math.max(0, total - shown);
  }

  /** Saves every file of `overview` that matched no row, one per line
   * under a header, by name and whether an object links it already, as a
   * CSV named after the source file without its extension. A spreadsheet
   * opens a name that starts like a formula as text. */
  downloadFilesWithoutRow(overview: ImportModuleMediaOverview): void {
    const t = (key: string) => this.translate.instant(key);
    const yes = t('TRANS__FIELD__BOOLEAN__YES');
    const no = t('TRANS__FIELD__BOOLEAN__NO');
    downloadCsv(
      `${fileStem(this.fileName) || 'raw_import'}_files_without_row.csv`,
      [
        [t('TRANS__FIELD__MEDIA__FILE_NAME'), t('TRANS__IMPORT__PREFLIGHT_MEDIA_OVERVIEW_CSV_LINKED')],
        ...overview.files_without_row.map(file => [file.file_name, file.linked ? yes : no]),
      ],
      {formulasAsText: true});
  }

  /** The target label for a `created_revision` kind. */
  createdRevisionKindLabel(kind: string): string {
    return createdRevisionKindLabel(kind, this.translate);
  }

  /** Narrows the row list to `group`'s rows, or back to every row with a
   * warning when `group` is the one selected. */
  selectGroup(group: WarningGroup): void {
    this.selectedGroup = this.selectedGroup === group ? null : group;
    const rows = this.selectedGroup ? new Set(this.selectedGroup.rows) : null;
    this.listedRows = rows ? this.view.warningRows.filter(r => rows.has(r.row.row)) : this.view.warningRows;
    this.shownRows = WARNING_ROWS_FIRST_SHOWN;
  }

  /** Opens the step view where `group`'s warning is fixed. */
  openFix(group: WarningGroup): void {
    if (group.fix?.kind === 'view') {
      this.openView.emit(group.fix.view);
    }
  }

  clearGroup(): void {
    if (this.selectedGroup) {
      this.selectGroup(this.selectedGroup);
    }
  }

  showMoreRows(): void {
    this.shownRows = showMore(this.shownRows, ROWS_STEP, this.listedRows.length);
  }

  showAllRows(): void {
    this.shownRows = this.listedRows.length;
  }

  toggleRowValues(rowView: PreflightRowView): void {
    const key = rowView.row.row;
    if (!this.openRows.delete(key)) {
      this.openRows.add(key);
    }
  }

  toggleAllRows(): void {
    this.allRowsOpen = !this.allRowsOpen;
  }

  showMoreOfAllRows(): void {
    this.allRowsShown = showMore(this.allRowsShown, ROWS_STEP, this.view.rows.length);
  }

  private buildView(): PreflightView {
    const result = this.preflightResult;
    const labels = cardLabels(result, this.resolveCardTitle, this.resolveSubBlockTitle);
    const labelOf = valueLabel(labels, this.translate.instant(MEDIA_VALUES_TITLE_KEY));
    const rows: PreflightRowView[] = result.rows.map(row => {
      const warnings = rowWarnings(row, labelOf);
      const warned = new Set(warnings.map(w => warnedValueKey(w.label, w.eventKey, w.subBlock)));
      const composed = row.composed.map(entry => {
        const label = labelOf(entry.label, entry.event_key, entry.on, entry.sub_block);
        const isWarned = warned.has(warnedValueKey(label, entry.event_key, entry.sub_block));
        return composedValueView(entry, label, partyInfoKey(row, entry), isWarned);
      });
      return {
        row,
        composed: composed.filter(view => view.entry.on !== 'media'),
        mediaComposed: composed.filter(view => view.entry.on === 'media'),
        events: row.events.map(key => ({key, title: labels.title(key)})),
        warnings,
      };
    });
    const warningRows = rows.filter(r => r.row.has_warnings);
    const split = result.errors ? this.splitErrors(result) : null;
    return {
      errors: split ? this.errorLabels(split.refusing) : [],
      notRefusingErrors: split ? this.errorLabels(split.notRefusing) : [],
      alwaysFiringEventGroups: result.event_groups
        .filter(g => g.always_fires)
        .map(g => ({key: g.key, title: labels.title(g.key)})),
      rows,
      warningRows,
      groups: groupWarnings(warningRows.map(r => ({row: r.row.row, warnings: r.warnings}))),
      mediaUnchecked: result.rows.some(row => row.media?.status === 'unknown'),
    };
  }

  /** The rows a result covers. A result that stopped short of its
   * range's end takes the range's count from `rangeCount`. */
  private buildCoverage(): PreflightCoverage {
    const result = this.preflightResult;
    const total = result.more_rows ? this.rangeCount : {rows: result.rows_previewed, exact: true};
    return {
      checked: result.rows_previewed,
      all: !result.more_rows,
      total: total ? rowCountText(total) : null,
      firstRow: result.rows[0]?.row ?? null,
      lastRow: result.rows.at(-1)?.row ?? null,
    };
  }

  /** Every block-level error of `e` (a stale field, a bad lookup table, a
   * card that can never write, a review decision the run would reject, a
   * right the user lacks), a target field by its label and a card and a
   * right by its line, listed in the order `labels` names the error keys. */
  private errorLabels(e: PreflightErrors): string[] {
    const labels: {[K in keyof PreflightErrors]-?: (errors: PreflightErrors) => string[]} = {
      target_fields: errors => errors.target_fields.map(id => this.resolveLabel(id)),
      lookups: errors => errors.lookups,
      groups: errors => errors.groups,
      incomplete_cards: errors => (errors.incomplete_cards ?? []).map(entry => this.describeIncompleteCard(entry)),
      media: errors => errors.media,
      media_pattern: errors => errors.media_pattern ? [mediaPatternErrorText(this.translate, errors.media_pattern)] : [],
      value_decisions: errors => errors.value_decisions ?? [],
      rights: errors => (errors.rights ?? []).map(entry => this.describeLackingRight(entry)),
    };
    return Object.values(labels).flatMap(label => label(e));
  }
}

/** `fileName` without its extension: what follows its last dot, unless
 * that dot starts the name. */
function fileStem(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
}

/** What a warning and the value it is about share: the label the panel
 * shows, the card and the sub-block. Two values of one field on one card
 * share a label where the card names no block, and differ by block. */
function warnedValueKey(label: string, eventKey: string | undefined, subBlock: string | undefined): string {
  return JSON.stringify([label, eventKey ?? '', subBlock ?? '']);
}

/** The note `row` shows under `entry` for the party name it is, by the
 * `party_warnings` entry with its label, card, sub-block, media and value;
 * `null` when it has none, or one that is a warning. */
function partyInfoKey(row: ImportModulePreflightRow, entry: ComposedEntry): string | null {
  const party = row.party_warnings.find(w =>
    w.label === entry.label && w.event_key === entry.event_key && w.sub_block === entry.sub_block
    && w.on === entry.on && w.value === entry.value);
  return party ? PARTY_INFO_KEYS[party.reason] ?? null : null;
}

/** `entry` under `label` as the panel shows it, with its `infoKey` and
 * whether it is `warned`. */
function composedValueView(
  entry: ComposedEntry, label: string, infoKey: string | null, warned: boolean,
): ComposedValueView {
  const ref = entry.kulturnav;
  if (!ref) {
    return {entry, label, text: entry.value, title: `${label}: ${entry.value}`, infoKey, warned};
  }
  const text = kulturnavRefText(ref);
  return {
    entry, label, text, title: `${label}: ${ref.name ? `${ref.name} (${ref.uuid})` : ref.uuid}`, infoKey, warned,
  };
}
